import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Settings, Save, Image, Palette } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DefaultBrandingSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    logo_url: '/radiuslink-logo.png',
    logo_scale: 1.0,
    company_name: 'RADIUSLINK',
    tagline: 'ISP Billing Management System',
    sidebar_color: '#000000',
    header_color: '#2563eb',
    header_gradient_start: '#3b82f6',
    header_gradient_end: '#0ea5e9'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/superadmin/default-branding`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success && response.data.data) {
        setSettings(prev => ({ ...prev, ...response.data.data }));
      }
    } catch (error) {
      console.error('Error fetching default branding:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/v2/superadmin/default-branding`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Default branding berhasil disimpan');
    } catch (error) {
      console.error('Error saving default branding:', error);
      toast.error('Gagal menyimpan default branding');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setSettings(prev => ({ ...prev, logo_url: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-32">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>;
  }

  const logoScale = settings.logo_scale || 1.0;
  const sidebarColor = settings.sidebar_color || '#000000';
  const headerGradientStart = settings.header_gradient_start || '#3b82f6';
  const headerGradientEnd = settings.header_gradient_end || '#0ea5e9';

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Settings className="w-5 h-5 text-blue-600" />
          Default Branding (Tenant Non-White Label)
        </CardTitle>
        <p className="text-sm text-gray-500">Pengaturan tampilan default untuk tenant yang tidak memiliki fitur white label</p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Image className="w-4 h-4 inline mr-1" />
                Logo Default
              </label>
              <div className="flex items-center gap-4">
                <div className="w-32 h-20 bg-gray-900 rounded-lg flex items-center justify-center overflow-hidden p-2">
                  {settings.logo_url && (
                    <img 
                      src={settings.logo_url} 
                      alt="Logo" 
                      className="max-h-full max-w-full object-contain"
                      style={{ transform: `scale(${logoScale})` }}
                    />
                  )}
                </div>
                <div>
                  <input type="file" id="default-logo" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                  <Button variant="outline" size="sm" onClick={() => document.getElementById('default-logo').click()}>
                    Ganti Logo
                  </Button>
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ukuran Logo: {logoScale.toFixed(1)}x
              </label>
              <input
                type="range"
                min="0.2"
                max="2.0"
                step="0.1"
                value={logoScale}
                onChange={(e) => setSettings(prev => ({ ...prev, logo_scale: parseFloat(e.target.value) }))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0.5x</span>
                <span>1.0x</span>
                <span>2.0x</span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nama Perusahaan</label>
              <Input
                value={settings.company_name || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, company_name: e.target.value }))}
                placeholder="RADIUSLINK"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tagline</label>
              <Input
                value={settings.tagline || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, tagline: e.target.value }))}
                placeholder="ISP Billing Management System"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Warna Sidebar</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={sidebarColor}
                    onChange={(e) => setSettings(prev => ({ ...prev, sidebar_color: e.target.value }))}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={sidebarColor}
                    onChange={(e) => setSettings(prev => ({ ...prev, sidebar_color: e.target.value }))}
                    className="font-mono text-sm"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Warna Header</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.header_color || '#2563eb'}
                    onChange={(e) => setSettings(prev => ({ ...prev, header_color: e.target.value, header_gradient_start: e.target.value, header_gradient_end: e.target.value }))}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={settings.header_color || '#2563eb'}
                    onChange={(e) => setSettings(prev => ({ ...prev, header_color: e.target.value, header_gradient_start: e.target.value, header_gradient_end: e.target.value }))}
                    className="font-mono text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <div className="text-xs text-gray-500 px-3 py-1 bg-gray-50">Preview</div>
          <div className="flex">
            <div className="w-48 p-4 flex flex-col items-center justify-center" style={{ backgroundColor: sidebarColor }}>
              {settings.logo_url && (
                <img src={settings.logo_url} alt="Preview" className="max-h-12 object-contain" style={{ transform: `scale(${logoScale})` }} />
              )}
              <span className="text-white text-xs mt-2 opacity-70">{settings.company_name}</span>
            </div>
            <div className="flex-1">
              <div className="px-4 py-2 text-white text-sm font-medium" style={{ background: `linear-gradient(90deg, ${headerGradientStart} 0%, ${headerGradientEnd} 100%)` }}>
                Dashboard
              </div>
              <div className="p-4 bg-gray-50 text-xs text-gray-400">Konten halaman...</div>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
            {saving ? (
              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Menyimpan...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" />Simpan Pengaturan</>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default DefaultBrandingSettings;
